'use client'

import { Copy24, Flag24, HorizontalDots24 } from '@vinted/monochrome-icons'
import { Button, Cell, Divider, Icon, Image, Rating, Spacer, Text } from '@vinted/web-ui'

import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { FormattedDateMessage, useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { UiState } from '@marketplace-web/shared/ui-state-util'

import DropdownMenu from '@marketplace-web/profile/dropdown-menu-ui'
import { Translation } from '@marketplace-web/i18n/i18n-data'

import { EDIT_FEEDBACK_URL, FEEDBACK_URL, NOTICE_AND_ACTION_FORM } from '../../../constants/routes'
import { FeedbackScreen, useFeedbackContext } from '../FeedbackProvider'
import Comment from './Comment'
import CommentInput from './CommentInput'
import DeleteFeedbackModal from './DeleteFeedbackModal'
import FeedbackActions from './FeedbackActions'
import FeedbackThumbnail from './FeedbackThumbnail'
import FeedbackTranslationButton from './FeedbackTranslationButton'
import FeedbackUsername from './FeedbackUsername'
import ModerateFeedbackModal from './ModerateFeedbackModal'

type Props = {
  id: number
  userId: number
  isContentTranslationEnabled: boolean
}

const FeedbackItem = ({ id, userId, isContentTranslationEnabled }: Props) => {
  const translate = useTranslate('user')
  const { baseUrl } = useBrowserNavigation()

  const {
    getFeedbackById,
    getFeedbackItemStateById,
    setScreenById,
    deleteFeedbackById,
    deleteCommentByFeedbackId,
    moderateFeedbackById,
  } = useFeedbackContext()
  const feedback = getFeedbackById(id)
  const screen = getFeedbackItemStateById(id)?.screen

  const isFeedbackReportingEnabled = useFeatureSwitch('feedback_reporting') && !feedback?.isSystem

  const handleCopyClick = () => {
    navigator.clipboard.writeText(`${baseUrl}${FEEDBACK_URL(id)}`)
  }

  const renderUsername = () => {
    if (!feedback) return null

    return <FeedbackUsername user={feedback.user} isSystem={feedback.isSystem} />
  }

  const renderThumbnail = () => {
    if (!feedback) return null

    return (
      <FeedbackThumbnail
        user={feedback.user}
        size={Image.Size.Large}
        isSystem={feedback.isSystem}
      />
    )
  }

  const renderDate = () => {
    if (!feedback) return null

    return <FormattedDateMessage date={new Date(feedback.createdAt)} relativeDate />
  }

  const renderRating = () => {
    if (!feedback) return null

    return (
      <Rating
        value={feedback.rating}
        aria={{
          'aria-label': feedback.rating
            ? translate('a11y.rating', { rating: feedback.rating, max_rating: 5 })
            : '',
        }}
      />
    )
  }

  const renderFeedback = () => {
    if (!feedback) return null

    return <Text as="span">{feedback.text}</Text>
  }

  const renderFeedbackWithDate = () => (
    <>
      {renderFeedback()}
      <Spacer size={Spacer.Size.Small} />
      <Text as="h3" type={Text.Type.Subtitle} theme="muted" text={renderDate()} />
    </>
  )

  const renderActions = () => {
    if (!feedback) return null

    return (
      <FeedbackActions
        id={id}
        editFeedbackUrl={EDIT_FEEDBACK_URL(userId, id)}
        canReply={feedback.canReply}
        canChange={feedback.canChange}
        canDelete={feedback.canDelete}
        canModerate={feedback.canModerate}
      />
    )
  }

  const renderComment = () => {
    switch (screen) {
      case FeedbackScreen.CreateComment:
      case FeedbackScreen.EditComment:
        return <CommentInput id={id} />
      default:
        return (
          feedback?.comment && (
            <Comment
              id={id}
              comment={feedback.comment}
              canChangeComment={feedback.canChangeComment}
              canDeleteComment={feedback.canDeleteComment}
            />
          )
        )
    }
  }

  const handleFeedbackDelete = () => {
    switch (screen) {
      case FeedbackScreen.DeleteFeedback:
        deleteFeedbackById(id)
        break
      case FeedbackScreen.DeleteComment:
        deleteCommentByFeedbackId(id)
        break
      default:
        break
    }
  }

  const handleFeedbackModerate = () => {
    const uiState = getFeedbackItemStateById(id)?.uiState

    if (!feedback || uiState === UiState.Pending) return

    switch (screen) {
      case FeedbackScreen.ModerateFeedback:
        moderateFeedbackById(id, feedback.feedbackUserId, feedback.userId)
        break

      default:
        break
    }
  }

  const handleCancel = () => {
    setScreenById(id, FeedbackScreen.View)
  }

  const renderDeleteFeedbackModal = () => (
    <DeleteFeedbackModal
      isOpen={screen === FeedbackScreen.DeleteFeedback || screen === FeedbackScreen.DeleteComment}
      onSubmit={handleFeedbackDelete}
      onClose={handleCancel}
    />
  )

  const renderModerateFeedbackModal = () => (
    <ModerateFeedbackModal
      isOpen={screen === FeedbackScreen.ModerateFeedback}
      onSubmit={handleFeedbackModerate}
      onClose={handleCancel}
    />
  )

  const renderTranslationButton = () => {
    if (
      !isContentTranslationEnabled ||
      !feedback?.localization ||
      feedback.localization === Translation.None
    )
      return null

    return (
      <>
        <Spacer />
        <FeedbackTranslationButton feedbackId={id} />
      </>
    )
  }

  const renderDropdownMenu = () => {
    const buttonProps = {
      icon: <Icon name={HorizontalDots24} />,
      styling: Button.Styling.Flat,
    }

    const dropdownActions = [
      {
        id: 'copy',
        text: <Text as="span" text={translate('feedback.copy')} theme="amplified" />,
        prefix: <Icon name={Copy24} />,
        onClick: handleCopyClick,
      },
      {
        id: 'report',
        text: <Text as="span" text={translate('feedback.report')} theme="amplified" />,
        prefix: <Icon name={Flag24} color={Icon.Color.Warning} />,
        url: NOTICE_AND_ACTION_FORM,
      },
    ]

    return (
      <DropdownMenu
        styling={Cell.Styling.Default}
        separator={<Divider />}
        actions={dropdownActions}
        buttonProps={buttonProps}
      />
    )
  }

  return (
    <Cell
      title={renderUsername()}
      prefix={renderThumbnail()}
      subtitle={isFeedbackReportingEnabled ? renderDropdownMenu() : renderDate()}
      styling={Cell.Styling.Tight}
      testId="feedback-item"
    >
      {renderRating()}
      <Spacer />
      {isFeedbackReportingEnabled ? renderFeedbackWithDate() : renderFeedback()}
      <Spacer />
      {renderActions()}
      <Spacer />
      {renderComment()}
      {renderTranslationButton()}
      {renderDeleteFeedbackModal()}
      {renderModerateFeedbackModal()}
    </Cell>
  )
}

export default FeedbackItem
